/**
 * Production board
 *
 * A designer-facing projection of the ticket state machine. The board does
 * not invent a second workflow or optimistically move cards: workers and
 * review decisions remain the authority for transitions.
 */

const BOARD_COLUMNS = [
  { key: "backlog", label: "Backlog", statuses: ["backlog"], hint: "Not scheduled yet" },
  { key: "ready", label: "Ready", statuses: ["ready"], hint: "Ready for an agent" },
  { key: "working", label: "Working", statuses: ["leased", "in_progress"], hint: "In production" },
  { key: "submitted", label: "Submitted", statuses: ["submitted"], hint: "Automated checks" },
  { key: "review", label: "Human review", statuses: ["review"], hint: "Waiting for a decision" },
  { key: "approved", label: "Approved", statuses: ["approved"], hint: "Accepted work" },
];

const EXCEPTIONS = ["blocked", "failed", "escalated", "stale", "rejected", "cancelled"];

export function initBoard({ api, toast, esc, openTicket = null, routeHref = null, onRouteStateChange = null }) {
  const root = document.getElementById("panel");
  let tickets = [];
  let query = localStorage.getItem("wl_board_query") || "";
  let type = localStorage.getItem("wl_board_type") || "all";
  let exception = EXCEPTIONS.includes(localStorage.getItem("wl_board_exception"))
    ? localStorage.getItem("wl_board_exception")
    : "";
  let loading = false;

  function getRouteState() {
    return {
      ...(query ? { query } : {}),
      ...(type !== "all" ? { type } : {}),
      ...(exception ? { exception } : {}),
    };
  }

  function publishRouteState() {
    onRouteStateChange?.(getRouteState());
  }

  function validType(value) {
    return value === "all" || ticketTypes().includes(value) ? value : "all";
  }

  async function render() {
    if (loading) return;
    loading = true;
    root.innerHTML = loadingShell();
    try {
      const projectId = document.body.dataset.projectId || "";
      if (!projectId) {
        root.innerHTML = `<div class="production-board production-empty"><h2>Select a project</h2><p>The Board is always scoped to one project.</p></div>`;
        return;
      }
      const response = await api(`/tickets?projectId=${encodeURIComponent(projectId)}`);
      tickets = Array.isArray(response.tickets) ? response.tickets : [];
      draw();
    } catch (error) {
      root.innerHTML = `<div class="production-board production-empty"><h2>Board unavailable</h2><p>${esc(readableError(error))}</p><button type="button" data-board-retry>Try again</button></div>`;
      root.querySelector("[data-board-retry]")?.addEventListener("click", render);
    } finally {
      loading = false;
    }
  }

  function loadingShell() {
    return `<main class="production-board" aria-busy="true">
      <header class="production-head"><div><p class="production-eyebrow">Production</p><h2>Board</h2></div></header>
      <div class="production-loading">Loading production work…</div>
    </main>`;
  }

  function draw() {
    const visible = filteredTickets();
    const exceptionTickets = exception
      ? visible.filter((ticket) => ticket.status === exception)
      : [];

    root.innerHTML = `<main class="production-board">
      <header class="production-head">
        <div>
          <p class="production-eyebrow">Production</p>
          <h2>Board</h2>
          <p>Every world task, from brief to approved result.</p>
        </div>
        <button type="button" data-board-refresh aria-label="Refresh board">Refresh</button>
      </header>

      <section class="board-controls" aria-label="Board controls">
        <label class="production-search">
          <span class="sr-only">Search work</span>
          <input type="search" value="${esc(query)}" placeholder="Search work, type, or world…" data-board-search />
        </label>
        <label>
          <span class="sr-only">Filter by work type</span>
          <select data-board-type>
            <option value="all">All work</option>
            ${ticketTypes().map((value) => `<option value="${esc(value)}" ${type === value ? "selected" : ""}>${esc(pretty(value))}</option>`).join("")}
          </select>
        </label>
      </section>

      <section class="board-exception-filters" aria-label="Exception filters">
        <span>Exceptions</span>
        ${EXCEPTIONS.map((status) => {
          const count = visible.filter((ticket) => ticket.status === status).length;
          return `<button type="button" class="${exception === status ? "on" : ""}" data-board-exception="${status}" aria-pressed="${exception === status}">
            ${esc(pretty(status))}<b>${count}</b>
          </button>`;
        }).join("")}
      </section>

      ${exception ? renderException(exception, exceptionTickets) : renderColumns(visible)}
    </main>`;

    bind();
  }

  function renderColumns(visible) {
    return `<p class="horizontal-scroll-hint">Swipe sideways to see every production stage →</p><section class="kanban" aria-label="Production workflow">
      ${BOARD_COLUMNS.map((column) => {
        const rows = visible
          .filter((ticket) => column.statuses.includes(ticket.status))
          .sort(sortTickets);
        return `<section class="kanban-column" data-column="${column.key}">
          <header>
            <div><h3>${column.label}</h3><p>${column.hint}</p></div>
            <span aria-label="${rows.length} tickets">${rows.length}</span>
          </header>
          <div class="kanban-stack">
            ${rows.length ? rows.map(ticketCard).join("") : `<div class="kanban-empty">No work here</div>`}
          </div>
        </section>`;
      }).join("")}
    </section>`;
  }

  function renderException(status, rows) {
    return `<section class="exception-view" aria-label="${esc(pretty(status))} work">
      <header>
        <div><p class="production-eyebrow">Exception view</p><h3>${esc(pretty(status))}</h3></div>
        <button type="button" data-board-clear-exception>Back to workflow</button>
      </header>
      <div class="exception-grid">
        ${rows.length ? rows.sort(sortTickets).map(ticketCard).join("") : `<div class="production-empty"><p>No ${esc(pretty(status).toLowerCase())} work matches this view.</p></div>`}
      </div>
    </section>`;
  }

  function ticketCard(ticket) {
    const title = presentationTitle(ticket);
    const scope = scopeLabel(ticket.scope);
    const updated = relativeTime(ticket.updatedAt || ticket.createdAt);
    const spend = Number(ticket.spentUsd || 0);
    const attempts = Number(ticket.attempt || 0);
    const maxAttempts = Number(ticket.maxAttempts || 0);
    const href = routeHref?.(ticket) || "#";
    return `<a class="kanban-card ${esc(ticket.status)}" href="${esc(href)}" data-board-ticket="${esc(ticket.id)}" data-board-ticket-name="${esc(title)}" aria-label="Open ${esc(title)}" style="color:inherit;text-decoration:none">
      <div class="kanban-card-top">
        <span class="work-type">${esc(pretty(ticket.type || "work"))}</span>
        <span class="work-priority" title="Priority ${esc(ticket.priority ?? 0)}">P${esc(ticket.priority ?? 0)}</span>
      </div>
      <h4>${esc(title)}</h4>
      <p>${esc(scope)}${ticket.worldId ? ` · World ${esc(shortId(ticket.worldId))}` : ""}</p>
      <footer>
        <span>${esc(updated)}</span>
        <span>${attempts}/${maxAttempts} tries${spend ? ` · $${spend.toFixed(2)}` : ""}</span>
      </footer>
    </a>`;
  }

  function bind() {
    root.querySelector("[data-board-refresh]")?.addEventListener("click", render);
    root.querySelector("[data-board-clear-exception]")?.addEventListener("click", () => setException(""));

    const search = root.querySelector("[data-board-search]");
    search?.addEventListener("input", () => {
      query = search.value;
      localStorage.setItem("wl_board_query", query);
      draw();
      publishRouteState();
      const nextSearch = root.querySelector("[data-board-search]");
      nextSearch?.focus();
      nextSearch?.setSelectionRange(query.length, query.length);
    });

    root.querySelector("[data-board-type]")?.addEventListener("change", (event) => {
      type = event.currentTarget.value;
      localStorage.setItem("wl_board_type", type);
      draw();
      publishRouteState();
    });

    root.querySelectorAll("[data-board-exception]").forEach((button) => {
      button.addEventListener("click", () => setException(button.dataset.boardException === exception ? "" : button.dataset.boardException));
    });

    root.querySelectorAll("[data-board-ticket]").forEach((card) => {
      const open = () => {
        if (typeof openTicket === "function") openTicket(card.dataset.boardTicket, card.dataset.boardTicketName);
        else toast?.("Ticket detail is not connected yet", true);
      };
      card.addEventListener("click", (event) => {
        if (event.button !== 0 || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        open();
      });
    });
  }

  function setException(value) {
    exception = value || "";
    localStorage.setItem("wl_board_exception", exception);
    draw();
    publishRouteState();
  }

  function filteredTickets() {
    const needle = query.trim().toLowerCase();
    return tickets.filter((ticket) => {
      if (type !== "all" && ticket.type !== type) return false;
      if (!needle) return true;
      const searchable = [ticket.title, ticket.type, ticket.status, ticket.worldId, ticket.id, scopeLabel(ticket.scope)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(needle);
    });
  }

  function ticketTypes() {
    return [...new Set(tickets.map((ticket) => ticket.type).filter(Boolean))].sort();
  }

  function applyRouteState(state = {}) {
    query = String(state.query || "");
    type = validType(String(state.type || "all"));
    exception = EXCEPTIONS.includes(state.exception) ? state.exception : "";
    localStorage.setItem("wl_board_query", query);
    localStorage.setItem("wl_board_type", type);
    localStorage.setItem("wl_board_exception", exception);
    if (tickets.length) draw();
    else return render();
  }

  return { render, getRouteState, applyRouteState };
}

function presentationTitle(ticket) {
  const title = String(ticket?.title || ticket?.id || "World work");
  // Preserve stored execution history while keeping retired implementation
  // terminology out of the designer-facing production board.
  return title.replace(/^Map pipeline\s*:/i, "World plan:");
}

function sortTickets(a, b) {
  const priority = Number(b.priority || 0) - Number(a.priority || 0);
  if (priority) return priority;
  return String(b.updatedAt || b.createdAt || "").localeCompare(String(a.updatedAt || a.createdAt || ""));
}

function scopeLabel(scope) {
  if (!scope || scope.kind === "global") return "Whole project";
  if (scope.kind === "cell") return `Section ${scope.cellId || ""}`.trim();
  if (scope.kind === "asset") return `Asset ${shortId(scope.assetUid)}`;
  if (scope.kind === "placement") return `Placement ${shortId(scope.placementUid)}`;
  return "Project work";
}

function pretty(value) {
  const text = String(value || "").replaceAll("_", " ");
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : "";
}

function shortId(value) {
  return String(value || "").slice(0, 8);
}

function relativeTime(value) {
  if (!value) return "Not started";
  const delta = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(delta)) return "Recently";
  const minutes = Math.max(0, Math.floor(delta / 60_000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function readableError(error) {
  return error instanceof Error ? error.message : String(error);
}
